import type { ColumnDef } from "@tanstack/react-table";
import { Check, X, ImageOff, MessageSquare } from "lucide-react";
import { getSportLabel } from "@/lib/sport-defaults";
import { verdictColor, verdictLabel, type CityReportStation } from "./types";

function sportAt(row: CityReportStation, idx: number): string {
  const locker = row.lockers[idx];
  if (!locker || !locker.sport_type) return "";
  return getSportLabel(locker.sport_type);
}

function SportCell({ value }: { value: string }) {
  return (
    <span className="text-sm text-muted-foreground capitalize">
      {value || "—"}
    </span>
  );
}

export const stationColumns: ColumnDef<CityReportStation>[] = [
  {
    id: "photo",
    header: "",
    cell: ({ row }) => {
      const first = row.original.photos[0];
      return (
        <div className="size-10 shrink-0 rounded-md overflow-hidden bg-muted flex items-center justify-center">
          {first ? (
            <img
              src={first.url}
              alt=""
              className="size-full object-cover"
              loading="lazy"
            />
          ) : (
            <ImageOff className="size-4 text-muted-foreground/60" />
          )}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: "label",
    accessorKey: "label",
    header: "Station",
    cell: ({ row }) => (
      <div className="min-w-0">
        <div className="font-medium truncate">{row.original.label}</div>
        {row.original.address && (
          <div className="text-xs text-muted-foreground truncate max-w-[240px]">
            {row.original.address}
          </div>
        )}
      </div>
    ),
  },
  {
    id: "sport_1",
    header: "Sport 1",
    accessorFn: (row) => sportAt(row, 0),
    cell: ({ getValue }) => <SportCell value={getValue<string>()} />,
    enableSorting: false,
  },
  {
    id: "sport_2",
    header: "Sport 2",
    accessorFn: (row) => sportAt(row, 1),
    cell: ({ getValue }) => <SportCell value={getValue<string>()} />,
    enableSorting: false,
  },
  {
    id: "sport_3",
    header: "Sport 3",
    accessorFn: (row) => sportAt(row, 2),
    cell: ({ getValue }) => <SportCell value={getValue<string>()} />,
    enableSorting: false,
  },
  {
    id: "verdict",
    accessorKey: "city_verdict",
    header: "Verdict",
    cell: ({ row }) => {
      const v = row.original.city_verdict;
      const color = verdictColor(v);
      return (
        <span
          className="inline-flex items-center gap-1.5 text-xs font-medium"
          style={{ color }}
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: color }}
          />
          {verdictLabel(v)}
        </span>
      );
    },
  },
  {
    id: "comment_count",
    accessorKey: "comment_count",
    header: "Comments",
    cell: ({ getValue }) => {
      const n = getValue<number>();
      if (n === 0)
        return <span className="text-muted-foreground text-sm">—</span>;
      return (
        <span className="inline-flex items-center gap-1 text-sm">
          <MessageSquare className="size-3.5 text-muted-foreground" />
          {n}
        </span>
      );
    },
  },
];

export { Check, X };
